import { getCookieItem, getLocalItem } from "@/lib/commonService";
import { isLogin } from "@/src/models/loginModel";

// 事件类型定义（与 DataAcquisition.ts 保持一致）
type EventType =
    | "addWishlist"
    | "clickProduct"
    | "addCart"
    | "tryOn"
    | "createOrder"
    | "paidOrder";

// 事件行为类型，CLK: 点击，EXP: 曝光，OTHER: 其他
type EventAcitonType = "CLK" | "EXP" | "OTHER";

// 获取用户类型的辅助函数（与 DataAcquisition.ts 保持一致）
const getUserType = (): string => {
    if (!isLogin()) {
        return "4";
    }
    const is_old_customer = getLocalItem("is_old_customer") || false;
    return is_old_customer ? "2" : "5";
};

class QuickTracking {
    initStatus: boolean = false;
    loadPromise: Promise<boolean> | null = null;

    // 初始化参数
    trackDomain: string = "https://quickaplus-he-api-cn-shanghai.aliyuncs.com";

    /**
     * 动态加载 QuickTracking SDK 脚本（参考 Cybertargeter.ts 实现）
     */
    loadSDKScript(): void {
        const head = document.getElementsByTagName("HEAD").item(0);
        if (!head) {
            console.error("QuickTracking: Cannot find HEAD element");
            return;
        }

        // 初始化 aplus_queue 全局队列
        window.aplus_queue = window.aplus_queue || [];

        // 创建 script 标签加载 SDK
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.id = "beacon-aplus";
        script.src = "/static/qt_web.umd.js";

        head.appendChild(script);
    }

    /**
     * 配置 SDK 初始化参数
     */
    configureSDK(): void {
        // 设置 appKey
        window.aplus_queue.push({
            action: "aplus.setMetaInfo",
            arguments: ["appKey", window.getSiteConfiguration("QuickTracking")],
        });

        // 设置收数域名
        window.aplus_queue.push({
            action: "aplus.setMetaInfo",
            arguments: ["trackDomain", this.trackDomain],
        });

        // window.aplus_queue.push({
        //   action: 'aplus.setMetaInfo',
        //   arguments: ['DEBUG', true]
        // });
    }

    /**
     * 检测 QuickTracking SDK 是否加载完成（参考 Cybertargeter.ts 中 mkqLoadedPromise 实现）
     */
    aplusLoadedPromise(): Promise<boolean> {
        let timer: any;
        let attempts = 0;
        const maxAttempts = 200; // 10 seconds / 50ms = 200 attempts

        return new Promise((resolve, reject) => {
            if (window && window.aplus) {
                resolve(true);
            } else {
                timer = setInterval(() => {
                    attempts++;
                    if (window && window.aplus) {
                        resolve(true);
                        clearInterval(timer);
                    } else if (attempts >= maxAttempts) {
                        reject(false);
                        console.log("QuickTracking SDK not loaded");
                        clearInterval(timer);
                    }
                }, 50);
            }
        });
    }

    /**
     * 初始化 QuickTracking SDK
     * @returns Promise<boolean> 初始化是否成功
     */
    init(): Promise<boolean> {
        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = new Promise((resolve, reject) => {
            try {
                if (!window.getSiteConfiguration("QuickTracking")) {
                    resolve(false);
                    return;
                }

                // 如果已经初始化，直接返回成功
                if (this.initStatus) {
                    resolve(true);
                    return;
                }

                // 加载 SDK 脚本
                this.loadSDKScript();

                // 配置 SDK 参数
                this.configureSDK();

                // 等待 SDK 加载完成
                this.aplusLoadedPromise()
                    .then(() => {
                        // 设置用户相关信息
                        const userid = getCookieItem("user_id") || null;
                        if (userid) {
                            window.aplus_queue.push({
                                action: "aplus.setMetaInfo",
                                arguments: ["_user_id", userid],
                            });
                        }

                        // 设置用户类型
                        window.aplus_queue.push({
                            action: "aplus.setMetaInfo",
                            arguments: ["user_type", getUserType()],
                        });

                        this.initStatus = true;
                        resolve(true);
                    })
                    .catch((error) => {
                        console.error(
                            "QuickTracking SDK initialization failed:",
                            error,
                        );
                        this.initStatus = false;
                        reject(false);
                    });
            } catch (error) {
                console.error("QuickTracking init error:", error);
                this.initStatus = false;
                reject(false);
            }
        });

        return this.loadPromise;
    }

    /**
     * 用户登录（参考 DataAcquisition.ts）
     * @param name 用户名
     * @param id 用户ID
     */
    login(id: string | number): void {
        try {
            this.init()
                .then(() => {
                    if (this.initStatus) {
                        const userid = getCookieItem("user_id") || null;

                        window.aplus_queue.push({
                            action: "aplus.setMetaInfo",
                            arguments: ["_user_id", userid],
                        });
                    }
                })
                .catch((error) => {
                    console.error("QuickTracking login failed:", error);
                });
        } catch (error) {
            console.error("Error occurred in login:", error);
        }
    }

    /**
     * 用户退出登录（参考 DataAcquisition.ts）
     */
    OutLogin(): void {
        try {
            this.init()
                .then(() => {
                    if (this.initStatus) {
                        window.aplus_queue.push({
                            action: "aplus.setMetaInfo",
                            arguments: ["_user_id", ""],
                        });
                    }
                })
                .catch((error) => {
                    console.error("QuickTracking logout failed:", error);
                });
        } catch (error) {
            console.error("Error occurred in OutLogin:", error);
        }
    }

    /**
     * 通用事件记录方法（参考 DataAcquisition.ts）
     * @param name 事件类型
     * @param id 商品ID或ID数组
     */
    record(name: EventType, id: string | string[]): void {
        try {
            console.log("record", name, id);

            this.init()
                .then(() => {
                    if (this.initStatus) {
                        const itemId = Array.isArray(id) ? id.join(",") : id;

                        window.aplus_queue.push({
                            action: "aplus.record",
                            arguments: [name, "CLK", { item_id: itemId }],
                        });

                        console.log("QuickTracking record:", name, itemId);
                    }
                })
                .catch((error) => {
                    console.error("QuickTracking record failed:", error);
                });
        } catch (error) {
            console.error("Error occurred in record:", error);
        }
    }

    /**
     * 自定义事件记录（参考 DataAcquisition.ts）
     * @param name 事件名称
     * @param attributes 事件属性
     */
    customEvent(
        name: string,
        eventAcitonType: EventAcitonType = "CLK",
        attributes: Record<string, any>,
    ): void {
        try {
            this.init()
                .then(() => {
                    if (this.initStatus) {
                        window.aplus_queue.push({
                            action: "aplus.record",
                            arguments: [name, eventAcitonType, attributes],
                        });

                        console.log(
                            "QuickTracking customEvent:",
                            name,
                            attributes,
                        );
                    }
                })
                .catch((error) => {
                    console.error("QuickTracking customEvent failed:", error);
                });
        } catch (error) {
            console.error("Error occurred in customEvent:", name, error);
        }
    }

    // ==================== 具体埋点事件方法（与 DataAcquisition.ts 保持一致） ====================

    /**
     * 收藏商品（参考 DataAcquisition.ts）
     * @param id 商品ID
     */
    addWishlist(id: string): void {
        this.record("addWishlist", id);
    }

    /**
     * 点击商品（参考 DataAcquisition.ts）
     * @param id 商品ID
     */
    clickProduct(id: string): void {
        this.record("clickProduct", id);
    }

    /**
     * 添加到购物车（参考 DataAcquisition.ts）
     * @param id 商品ID
     */
    addCart(id: string): void {
        this.record("addCart", id);
    }

    /**
     * 试戴商品（参考 DataAcquisition.ts）
     * @param id 商品ID
     */
    tryOn(id: string): void {
        this.record("tryOn", id);
    }

    /**
     * 创建订单（参考 DataAcquisition.ts）
     * @param id 商品ID或ID数组
     * @param orders_oid 订单ID
     */
    createOrder(id: string | string[], orders_oid: string | number): void {
        try {
            this.init()
                .then(() => {
                    if (this.initStatus) {
                        const items = Array.isArray(id)
                            ? id.map((item) => ({ id: item }))
                            : [{ id: id }];

                        window.aplus_queue.push({
                            action: "aplus.record",
                            arguments: [
                                "createOrder",
                                "CLK",
                                {
                                    items: JSON.stringify(items),
                                    orders_oid: orders_oid,
                                },
                            ],
                        });

                        console.log(
                            "QuickTracking createOrder:",
                            name,
                            id,
                            orders_oid,
                        );
                    }
                })
                .catch((error) => {
                    console.error("QuickTracking createOrder failed:", error);
                });
        } catch (error) {
            console.error("Error occurred in createOrder:", error);
        }
    }

    /**
     * 支付成功（参考 DataAcquisition.ts）
     * @param id 商品ID
     * @param orders_oid 订单ID
     */
    paidOrder(id: string, orders_oid: string | number): void {
        try {
            this.init()
                .then(() => {
                    if (this.initStatus) {
                        window.aplus_queue.push({
                            action: "aplus.record",
                            arguments: [
                                "paidOrder",
                                "CLK",
                                {
                                    item_id: id,
                                    orders_oid: orders_oid,
                                },
                            ],
                        });

                        console.log("QuickTracking paidOrder:", id, orders_oid);
                    }
                })
                .catch((error) => {
                    console.error("QuickTracking paidOrder failed:", error);
                });
        } catch (error) {
            console.error("Error occurred in paidOrder:", error);
        }
    }
}

// 创建并导出 QuickTracking 实例（参考 Cybertargeter.ts 和 DataAcquisition.ts 的导出方式）
const quickTracking = new QuickTracking();

export default quickTracking;
